// frontend/src/components/ConsultModal.jsx
import { useState, useEffect } from 'react';
import { X, UserPlus } from 'lucide-react';
import api from '../api/client';
import { useAuth } from './AuthContext';

export default function ConsultModal({ open, onClose, symptomId, excludeIds = [], onSuccess }) {
  const { user } = useAuth();
  const [doctors, setDoctors] = useState([]);
  const [specialty, setSpecialty] = useState('');
  const [selectedDoctorId, setSelectedDoctorId] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setError('');
    setSelectedDoctorId('');
    setSpecialty('');
    setNote('');
    api.get('/profile/doctors')
      .then(res => setDoctors(res.data.data || []))
      .catch(() => setError('Failed to load doctors. Please try again.'));
  }, [open]);

  if (!open) return null;

  const currentUserId = user?._id || user?.id || '';
  const specialties = [...new Set(doctors.map(d => d.specialization).filter(Boolean))].sort();
  const filtered = doctors.filter(d => {
    if (d._id === currentUserId) return false;
    if (excludeIds.includes(d._id)) return false;
    if (specialty && d.specialization !== specialty) return false;
    return true;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDoctorId || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      await api.post(`/responses/${symptomId}/consult`, {
        consultedDoctorId: selectedDoctorId,
        note,
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send consult request');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-violet-100 flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-violet-600" />
            </div>
            <h2 className="font-bold text-slate-900">Request Consult</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-slate-100 transition-colors"
          >
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <p className="text-xs text-red-600 bg-red-50 px-4 py-3 rounded-xl">{error}</p>
          )}

          <div>
            <label className="block text-xs font-extrabold text-slate-500 uppercase tracking-widest mb-2">
              Specialty
            </label>
            <select
              value={specialty}
              onChange={e => { setSpecialty(e.target.value); setSelectedDoctorId(''); }}
              className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm bg-slate-50 focus:ring-2 focus:ring-violet-200 focus:border-violet-400"
            >
              <option value="">All specialties</option>
              {specialties.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs font-extrabold text-slate-500 uppercase tracking-widest mb-2">
              Select Doctor
            </label>
            <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
              {filtered.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-6">No doctors available</p>
              ) : filtered.map(d => (
                <button
                  key={d._id}
                  type="button"
                  onClick={() => setSelectedDoctorId(d._id)}
                  className={`w-full text-left px-4 py-3 rounded-xl border transition-all text-sm font-semibold ${
                    selectedDoctorId === d._id
                      ? 'border-violet-400 bg-violet-50 text-violet-900'
                      : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300'
                  }`}
                >
                  Dr. {d.name}
                  {d.specialization && (
                    <span className="ml-2 text-xs text-slate-400 font-normal">{d.specialization}</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-extrabold text-slate-500 uppercase tracking-widest mb-2">
              Note <span className="normal-case font-normal text-slate-400">(optional)</span>
            </label>
            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              maxLength={300}
              rows={2}
              placeholder="e.g. Please review for possible cardiac involvement"
              className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm bg-slate-50 focus:ring-2 focus:ring-violet-200 focus:border-violet-400 resize-none"
            />
            <p className="text-xs text-slate-400 mt-1">{note.length}/300</p>
          </div>

          <button
            type="submit"
            disabled={!selectedDoctorId || submitting}
            className="w-full py-3 bg-violet-600 text-white text-sm font-bold rounded-xl hover:bg-violet-700 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? 'Sending…' : 'Send Consult Request'}
          </button>
        </form>
      </div>
    </div>
  );
}
